import {type AutoColor, type IconName} from '@wandb/components';

/**
 * AgentSuggestion is one new-chat suggestion chip. `prompt` is the text sent on
 * click and defaults to `title`.
 */
export interface AgentSuggestion {
  title: string;
  prompt?: string;
  icon: IconName;
  color: AutoColor;
}

// Color encodes a chip's intent, so a row's mix of actions reads at a glance.
// sienna=diagnose, gold=insight, teal=summarize, blue=find, cactus=status,
// green=create, purple=configure, magenta=ask-the-agent.

// Shared

export const summarizeReport: AgentSuggestion = {
  title: 'Summarize this project in a report',
  prompt: '/summarize-project',
  icon: 'report',
  color: 'teal',
};

export const patterns: AgentSuggestion = {
  title: 'What patterns do you see in my results?',
  prompt: '/find-patterns',
  icon: 'chart-line',
  color: 'gold',
};

export const whatCanYouDo: AgentSuggestion = {
  title: 'What can you do?',
  prompt: '/introduce-wb-agent',
  icon: 'help-alt',
  color: 'magenta',
};

export const createSection: AgentSuggestion = {
  title: 'Create a section of panels with outliers',
  icon: 'chart-box-plot',
  color: 'green',
};

export const roastMyProject: AgentSuggestion = {
  title: 'Roast my project',
  prompt: `Roast this ML/AI project in up to 120 words — fewer is better. Cut any line that doesn't carry evidence or extend the central image. Don't pad to hit the limit.

Real evidence only. State every metric in normalized, human-readable form — a percentage or a single clean decimal, never a raw numerator-over-denominator and never sixteen-digit precision. Reduce and round sensibly (17%, not 222.0/1,275 or 0.30451047068). If two figures express the same metric, report it once. Round for readability, never toward the joke.

Find the biggest flaw first. Then build the analogy as a mechanism, not a vibe:

Name the real cause → effect in the data (e.g. "narrow detection threshold → high precision but misses most objects"). Find an image with the SAME cause → effect, where the funny thing happens for the same reason the real thing happens. Verify the mechanism transfers: does the agent in your image behave the way the metric behaves, for the same underlying reason?

- "Velvet-rope club that stays accurate by barely letting anyone in" ✓ — a reader can recover high-precision-low-recall from the image. The mechanism matches.
- "Traffic cop who waves through only the cars parked on his shoes" ✗ — that cop would arrest those cars, not wave them through. Sounds cop-shaped, but the behavior contradicts the metric. Broken.

The test: a reader should be able to reconstruct the actual failure from the image alone. If they can't, pick another image.

Then:

Swap test — if the analogy would fit any other broken project, it's too generic. Kill it.

If the project has one dominant flaw, extend one image. If it has two or three distinct flaws, state them plainly and use an analogy only where one earns its place — a forced single image across unrelated problems is worse than no image. Every metric you cite must reinforce the image; don't staple unrelated jokes together.

Don't repeat the analogy's key word in the opening and closing lines — extend the idea, don't echo the word.

Don't let a tidy contrast launder a bad number into a virtue: if a "good" metric only looks good because another is catastrophic, name it as the same failure, not a silver lining.`,
  icon: 'explosion',
  color: 'purple',
};

export const recommendNextExperiment: AgentSuggestion = {
  title: 'Recommend my next experiment',
  prompt: '/recommend-next-experiment',
  icon: 'lightbulb-info',
  color: 'blue',
};

export const autoresearchQuickstart: AgentSuggestion = {
  title: 'Launch experiments with autoresearch',
  prompt: '/autoresearch-quickstart',
  icon: 'rocket-launch',
  color: 'blue',
};

// Default

export const recentCrashedRuns: AgentSuggestion = {
  title: 'Have any of my recent runs crashed?',
  icon: 'warning',
  color: 'sienna',
};

export const starredReports: AgentSuggestion = {
  title: 'Find the reports I starred',
  icon: 'star',
  color: 'blue',
};

export const recentlyFinishedRuns: AgentSuggestion = {
  title: 'Which runs finished most recently?',
  icon: 'recent-clock',
  color: 'cactus',
};

// Runs table

export const setBaseline: AgentSuggestion = {
  title: 'Set my best run as the baseline',
  icon: 'baseline-alt',
  color: 'purple',
};

// Run logs

export const analyzeLogs: AgentSuggestion = {
  title: 'Analyze my logs',
  icon: 'list-bullets',
  color: 'sienna',
};

// Run (Overview)

export const compareRecentRuns: AgentSuggestion = {
  title: 'How does this run compare to recent runs?',
  icon: 'chart-scatterplot',
  color: 'cactus',
};

// Non-project / org / profile

export const crashedRuns: AgentSuggestion = {
  title: 'How many of my runs crashed this past week?',
  icon: 'warning',
  color: 'sienna',
};

export const activeRuns: AgentSuggestion = {
  title: 'Find all of my active runs',
  icon: 'running-repeat',
  color: 'cactus',
};

export const starProjects: AgentSuggestion = {
  title: 'Star the projects I visit the most',
  icon: 'star',
  color: 'purple',
};

// Registry

export const collectionsActivity: AgentSuggestion = {
  title: 'Show me collections with recent activity',
  icon: 'recent-clock',
  color: 'cactus',
};

export const usefulRegistries: AgentSuggestion = {
  title: 'Show me the most useful registries and collections for me',
  icon: 'magic-wand-star',
  color: 'magenta',
};

export const cleanupRegistries: AgentSuggestion = {
  title: 'Help me clean up my registries',
  icon: 'delete',
  color: 'purple',
};

export const registriesWithAutomations: AgentSuggestion = {
  title: 'Which Registries have Automations?',
  icon: 'automation-robot-arm',
  color: 'blue',
};

// Automations

export const understandAutomations: AgentSuggestion = {
  title: 'Help me understand these automations',
  icon: 'automation-robot-arm',
  color: 'teal',
};

export const createAutomation: AgentSuggestion = {
  title: 'Help me create an automation',
  icon: 'add-new',
  color: 'green',
};

export const describeAutomations: AgentSuggestion = {
  title: 'Describe the existing automations',
  icon: 'info',
  color: 'blue',
};

export const configureGithubAction: AgentSuggestion = {
  title: 'Help me configure a Github action',
  icon: 'github',
  color: 'purple',
};

// Weave (conversations)

export const frustratedUsers: AgentSuggestion = {
  title: 'What did frustrated users say?',
  icon: 'thumbs-down',
  color: 'sienna',
};

export const summarizeConversations: AgentSuggestion = {
  title: 'Summarize the last conversation/traces',
  icon: 'forum-chat-bubble',
  color: 'teal',
};

// Weave (signals)

export const createSignal: AgentSuggestion = {
  title: 'Create a new signal',
  icon: 'add-new',
  color: 'green',
};

export const signalsFound: AgentSuggestion = {
  title: 'What signals have you found?',
  icon: 'search',
  color: 'blue',
};

// Weave (evaluations)

export const createDataset: AgentSuggestion = {
  title: 'Help me create a dataset',
  icon: 'table',
  color: 'green',
};

export const createScorers: AgentSuggestion = {
  title: 'Help me create scorers',
  icon: 'number-hashtag',
  color: 'purple',
};

export const evalImproved: AgentSuggestion = {
  title: 'Did my latest eval improve and why?',
  icon: 'chart-line',
  color: 'gold',
};

export const whatToTryNext: AgentSuggestion = {
  title: 'What should I try next?',
  icon: 'lightbulb-info',
  color: 'magenta',
};
